import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config'; 
import { 
    collection, 
    query, 
    doc, 
    orderBy, 
    onSnapshot 
} from 'firebase/firestore';

// IMPORTAMOS LA UTILIDAD DE BORRADO PROFUNDO
import { deleteSiniestroFull } from '../../../../firebase/firebaseUtils';

import ModalSiniestro from '../../../ui/ModalSiniestro';
import ModalNotificacion from '../../../ui/ModalNotificacion'; 
import ModalConfirmacion from '../../../ui/ModalConfirmacion'; 
import { getSiniestroStatusChip, getPersonaChipClass } from '../../../../utils/styles';

// --- ICONOS ---
const IconAlert = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
const IconHistory = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

/**
 * SiniestrosTab - Gestión de siniestros con actualización en tiempo real.
 * Escucha cambios en la colección de siniestros y en sus subcolecciones de gestiones.
 */
const SiniestrosTab = ({ poliza, client }) => {
    const [siniestros, setSiniestros] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editingSiniestro, setEditingSiniestro] = useState(null);

    const [expandedSiniestroId, setExpandedSiniestroId] = useState(null);
    const [gestionesHistorial, setGestionesHistorial] = useState({});
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [deletionConfirm, setDeletionConfirm] = useState({ show: false, siniestroToDelete: null });

    const polizaId = poliza.id;
    const clientId = client.id;

    // --- 1. LISTENER PRINCIPAL DE SINIESTROS (Real-time) ---
    useEffect(() => {
        if (!polizaId || !clientId) return;

        setLoading(true);
        const siniestrosRef = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/siniestros`);
        const q = query(siniestrosRef, orderBy('fechaSiniestro', 'desc')); 

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setSiniestros(data);
            setLoading(false);
        }, (error) => {
            console.error("Error cargando siniestros:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [polizaId, clientId]);
    
    // --- 2. LISTENER DEL HISTORIAL DE GESTIONES (Real-time al expandir) ---
    useEffect(() => {
        if (!expandedSiniestroId || !polizaId || !clientId) return;

        const gestionesRef = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/siniestros/${expandedSiniestroId}/gestiones`);
        // Ordenamos por fechaCreacion para asegurar el orden cronológico preciso
        const q = query(gestionesRef, orderBy('fechaCreacion', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setGestionesHistorial(prev => ({ 
                ...prev, 
                [expandedSiniestroId]: data 
            }));
        }, (error) => {
            console.error(`Error cargando gestiones:`, error);
        });

        return () => unsubscribe();
    }, [expandedSiniestroId, polizaId, clientId]);

    const confirmDelete = (siniestro) => setDeletionConfirm({ show: true, siniestroToDelete: siniestro });
    
    const handleDelete = async () => {
        const siniestroId = deletionConfirm.siniestroToDelete.id;
        const siniestroNum = deletionConfirm.siniestroToDelete.numeroSiniestro || siniestroId.substring(0, 5);
        setDeletionConfirm({ show: false, siniestroToDelete: null });

        try {
            const parentPath = `public/data/clientes/${clientId}/polizas/${polizaId}`;
            await deleteSiniestroFull(parentPath, siniestroId);
            setNotification({ show: true, message: `Siniestro N° ${siniestroNum} y su historial eliminados correctamente.`, type: 'success' });
        } catch (error) {
            console.error("Error al eliminar el siniestro:", error);
            setNotification({ show: true, message: 'Error al eliminar el siniestro completo.', type: 'error' });
        }
    };

    const handleModalSuccess = (msg, type = 'success') => {
        setEditingSiniestro(null);
        setShowModal(false);
        setNotification({ show: true, message: msg, type: type }); 
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        
        // Manejo de Timestamps de Firebase
        if (dateValue.seconds) {
            return new Date(dateValue.seconds * 1000).toLocaleDateString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
        
        // Manejo de objetos Date nativos
        if (dateValue instanceof Date) return dateValue.toLocaleDateString('es-AR');
        if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('es-AR');
        
        // Manejo de Strings (YYYY-MM-DD)
        if (typeof dateValue === 'string' && dateValue.includes('-')) {
            const parts = dateValue.split('-');
            return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateValue;
        }
        return dateValue;
    };
    
    const toggleRowExpansion = (siniestroId) => {
        setExpandedSiniestroId(expandedSiniestroId === siniestroId ? null : siniestroId);
    };

    return (
        <div className="space-y-6 animate-fade-in font-sans text-left pb-10">
            
            {/* HEADER Y ACCIONES */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-between items-center shadow-sm relative overflow-hidden shrink-0">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500"></div>
                <div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase">Gestión de Siniestros</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Historial y seguimiento de denuncias</p>
                </div>
                <button 
                    onClick={() => { setEditingSiniestro(null); setShowModal(true); }} 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg uppercase tracking-widest"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                    Cargar Siniestro
                </button>
            </div>

            {/* TABLA */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse text-[13px] min-w-[1000px]">
                    <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest">
                        <tr>
                            <th className="p-4 w-12 text-center"></th>
                            <th className="p-4 w-28 whitespace-nowrap">F. Gestión</th>
                            <th className="p-4 w-28 whitespace-nowrap">F. Siniestro</th>
                            <th className="p-4 w-48">Tipo de Siniestro</th>
                            <th className="p-4 w-40 text-center">Responsable</th>
                            <th className="p-4 w-32 text-center">N° Siniestro</th>
                            <th className="p-4 w-32 text-center">Estado del Siniestro</th>
                            <th className="p-4 w-24 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-5px_0_10px_rgba(0,0,0,0.3)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="8" className="p-16 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">Sincronizando siniestros...</td></tr>
                        ) : siniestros.length === 0 ? (
                            <tr><td colSpan="8" className="p-16 text-center text-slate-400 italic font-medium">No hay siniestros registrados.</td></tr>
                        ) : (
                            siniestros.map(siniestro => (
                                <React.Fragment key={siniestro.id}>
                                    <tr 
                                        onClick={() => toggleRowExpansion(siniestro.id)}
                                        className={`hover:bg-slate-50 transition-colors cursor-pointer group ${expandedSiniestroId === siniestro.id ? 'bg-blue-50/40' : ''}`}
                                    >
                                        <td className="p-4 text-center">
                                            <svg className={`w-3.5 h-3.5 transition-transform duration-300 ${expandedSiniestroId === siniestro.id ? 'rotate-90 text-blue-600' : 'text-slate-300 group-hover:text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                                        </td>
                                        <td className="p-4 font-bold text-slate-500 tabular-nums whitespace-nowrap">
                                            {formatDate(siniestro.fechaCarga)}
                                        </td>
                                        <td className="p-4 font-bold text-slate-800 tabular-nums whitespace-nowrap">
                                            {formatDate(siniestro.fechaSiniestro)}
                                        </td>
                                        <td className="p-4 font-bold text-blue-700 uppercase tracking-tight">
                                            {siniestro.tipoSiniestro || '-'}
                                        </td>
                                        <td className="p-4 text-center whitespace-nowrap">
                                            {siniestro.quienGestiono ? (
                                                <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(siniestro.quienGestiono)}`}>
                                                    {siniestro.quienGestiono}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="p-4 text-center font-bold text-slate-800">
                                            {siniestro.numeroSiniestro || '-'}
                                        </td>
                                        <td className="p-4 text-center whitespace-nowrap">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border ${getSiniestroStatusChip(siniestro.estadoGestion)}`}>
                                                {siniestro.estadoGestion}
                                            </span>
                                        </td>
                                        <td className={`p-4 text-right pr-6 sticky right-0 z-10 transition-colors shadow-[-5px_0_10px_rgba(0,0,0,0.02)] ${expandedSiniestroId === siniestro.id ? 'bg-[#f8faff]' : 'bg-white group-hover:bg-slate-50'}`}>
                                            <div className="flex justify-end gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                                <button onClick={(e) => { e.stopPropagation(); setEditingSiniestro(siniestro); setShowModal(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                <button onClick={(e) => { e.stopPropagation(); confirmDelete(siniestro); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                            </div>
                                        </td>
                                    </tr>
                                    
                                    {expandedSiniestroId === siniestro.id && (
                                        <tr className="bg-slate-50/50 border-b border-slate-100 animate-slide-down">
                                            <td colSpan="8" className="px-12 py-6 text-left">
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                    <div className="flex flex-col gap-2">
                                                        <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-widest flex items-center gap-2 mb-1"><IconAlert /> Detalle del Siniestro</h4>
                                                        <div className="pl-4 border-l-4 border-blue-100">
                                                            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm text-slate-600 text-sm leading-relaxed whitespace-pre-wrap italic">
                                                                "{siniestro.detalleSiniestro || 'Sin detalle especificado.'}"
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col gap-2">
                                                        <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-widest flex items-center gap-2 mb-1"><IconHistory /> Historial de Gestiones</h4>
                                                        <div className="pl-4 border-l-4 border-blue-100 space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                                            {gestionesHistorial[siniestro.id] ? (
                                                                gestionesHistorial[siniestro.id].length > 0 ? (
                                                                    gestionesHistorial[siniestro.id].map((g, idx) => (
                                                                        <div key={idx} className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm text-slate-700 text-[13px] flex gap-3 items-start animate-fade-in">
                                                                            <span className="font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-[10px] uppercase shrink-0 mt-0.5">
                                                                                {formatDate(g.fechaCreacion)}
                                                                            </span>
                                                                            <div className="flex flex-col">
                                                                                <span className="text-blue-800 font-black uppercase text-[11px] tracking-tight">{g.tipo || g.tipoGestion }</span>
                                                                                <span className="text-slate-600 font-medium leading-tight mt-1">{g.observacion}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                ) : <p className="text-slate-300 italic text-xs p-2">No hay gestiones registradas.</p>
                                                            ) : <div className="text-slate-300 text-xs font-bold animate-pulse p-2">Cargando historial...</div>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                .animate-slide-down { animation: slideDown 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            {/* MODALES */}
            <ModalSiniestro show={showModal} onClose={() => setShowModal(false)} onSuccess={handleModalSuccess} poliza={poliza} clientData={client} initialData={editingSiniestro} />
            <ModalNotificacion show={notification.show} onClose={() => setNotification({ show: false, message: '', type: 'success' })} message={notification.message} type={notification.type} />
            <ModalConfirmacion show={deletionConfirm.show} onClose={() => setDeletionConfirm({ show: false, siniestroToDelete: null })} onConfirm={handleDelete} title="Eliminar Siniestro" message="¿Seguro que desea eliminar este siniestro y todo su historial de gestiones? Esta acción es irreversible." />
        </div>
    );
};

export default SiniestrosTab;